import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { createPortal } from "react-dom";
import "../styles/SurfaceThresholdsModal.scss";
import { useThermalDesign } from "../../../ThermalDesignContext";
import { useSurfaceThresholdsModal } from "../hooks/ThermalDesignerHooks";
import { ThermalModalHeader, ThermalModalFooter } from "../components/common/ThermalDesignerCommonComponents";
import { DividerLine, EllipseIconWhite } from "../../../SatelliteProfileImgAssets/SatelliteProfileImgAssets";
import ConfirmationDialog from "../../../../../../CommonComponents/confirmation_dialog/ConfirmationDialog";
import { createColumnHelper, useReactTable, flexRender, getCoreRowModel } from "@tanstack/react-table";
import { showToastMessage } from "../../../../../../../utils/commonFunctions";
import type { PanelData } from "../types/ThermalConfig";
import { THRESHOLD_LEVELS, createEmptyThresholds, validateTemperatureValue, validateThresholdRange, validateAllThresholds } from "../types/SurfaceThresholdsTypes";
import type { Thresholds } from "../types/SurfaceThresholdsTypes";

type FilterMode = "face-deck" | "subsystem";

interface ThresholdRow {
  key: string;
  label: string;
  subLabel?: string;
  thresholds: Thresholds;
}

const ThresholdCell = ({
  initialValue,
  onCommit,
  columnId,
}: {
  initialValue: number | null;
  onCommit: (value: number | null) => void;
  columnId: string;
}) => {
  const [localValue, setLocalValue] = useState(initialValue ? String(initialValue) : "");
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setLocalValue(initialValue ? String(initialValue) : "");
    setHasError(false);
  }, [initialValue]);

  const handleChange = (raw: string) => {
    setHasError(false);
    if (raw.trim() === "") {
      setLocalValue("");
      return;
    }
    let sanitized = raw.replace(/[^0-9.-]/g, "");
    sanitized = sanitized[0] === "-" ? "-" + sanitized.slice(1).replace(/-/g, "") : sanitized.replace(/-/g, "");
    setLocalValue(sanitized);
  };

  const handleBlur = () => {
    const trimmed = localValue.trim();
    if (!trimmed || trimmed === "-") {
      setLocalValue("");
      onCommit(null);
      return;
    }

    const validation = validateTemperatureValue(localValue);
    if (!validation.isValid) {
      setHasError(true);
      showToastMessage("error", validation.error || "Invalid value");
      return;
    }

    const num = parseInt(localValue, 10);
    setLocalValue(String(num));
    onCommit(num);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      (e.target as HTMLInputElement).blur();
    } else if (e.key === "Tab") {
      e.preventDefault();
      const allInputs = Array.from(document.querySelectorAll(".threshold-input")) as HTMLInputElement[];
      const currentIndex = allInputs.findIndex((input) => input.id === columnId);

      if (e.shiftKey) {
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : allInputs.length - 1;
        allInputs[prevIndex]?.focus();
      } else {
        const nextIndex = currentIndex < allInputs.length - 1 ? currentIndex + 1 : 0;
        allInputs[nextIndex]?.focus();
      }
    }
  };

  return (
    <input
      id={columnId}
      type="text"
      value={localValue}
      onChange={(e) => handleChange(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder="—"
      className={`threshold-input ${hasError ? "threshold-input--error" : ""}`}
      title={hasError ? "Invalid value - check toast message" : ""}
      autoFocus={false}
    />
  );
};

const PanelSelectorButton = ({
  panel,
  isSelected,
  isConfigured,
  onSelect,
}: {
  panel: PanelData;
  isSelected: boolean;
  isConfigured: boolean;
  onSelect: (label: string) => void;
}) => (
  <button
    key={panel.panelLabel}
    type="button"
    className={`external-face-selector-item ${isSelected ? "isSelected" : ""}`}
    onClick={() => onSelect(panel.panelLabel)}
  >
    <div className="external-face-selector-item-text-div">
      <span>
        <EllipseIconWhite isActive={isConfigured} />
        {panel.displayName.split(" ")[0]}
      </span>
      <span>{panel.panelLabel}</span>
    </div>
  </button>
);

const SurfaceThresholdsModal = () => {
  const { mission_id, sat_id } = useParams();
  const { isSatDesignerSidebarOpen, setOpenModal } = useThermalDesign();

  const {
    isModalMinimized,
    setIsModalMinimized,
    thermalModel,
    subsystemThresholds,
    setSubsystemThreshold,
    applyPanelThreshold,
    handleSave: originalHandleSave,
    handleCancel: originalHandleCancel,
    handleExportCsv,
    handleImport: originalHandleImport,
  } = useSurfaceThresholdsModal();

  const [filterMode, setFilterMode] = useState<FilterMode>("face-deck");
  const [selectedPanelLabel, setSelectedPanelLabel] = useState<string | null>(null);
  const [isSaveConfirmationOpen, setIsSaveConfirmationOpen] = useState(false);
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false);

  const panelHasSubsystems = (panel: PanelData) =>
    (panel.cells ?? []).some((cell) => (cell.subsystems ?? []).length > 0);

  const isPanelThresholdsConfigured = (panel: PanelData) => {
    const names = new Set<string>();
    (panel.cells ?? []).forEach((cell) => (cell.subsystems ?? []).forEach((sub) => names.add(sub.displayName)));
    return names.size > 0 && Array.from(names).every((name) => subsystemThresholds[name] !== undefined);
  };

  const allPanels = useMemo(() => thermalModel?.panels ?? [], [thermalModel]);

  const facePanels = useMemo(
    () => allPanels.filter((p) => p.panelKind === "face" && panelHasSubsystems(p)),
    [allPanels]
  );
  const deckPanels = useMemo(
    () => allPanels.filter((p) => p.panelKind === "deck" && panelHasSubsystems(p)),
    [allPanels]
  );

  const facePanelRows = useMemo(() => {
    const rows: PanelData[][] = [];
    for (let i = 0; i < facePanels.length; i += 2) rows.push(facePanels.slice(i, i + 2));
    return rows;
  }, [facePanels]);

  useEffect(() => {
    if (filterMode !== "subsystem" || selectedPanelLabel) return;
    const firstAvailable = facePanels[0] ?? deckPanels[0];
    if (firstAvailable) setSelectedPanelLabel(firstAvailable.panelLabel);
  }, [filterMode, facePanels, deckPanels, selectedPanelLabel]);

  const selectedPanel = useMemo(
    () => allPanels.find((p) => p.panelLabel === selectedPanelLabel) ?? null,
    [allPanels, selectedPanelLabel]
  );

  const subsystemsOnSelectedPanel = useMemo(() => {
    if (!selectedPanel) return [];
    const seen = new Map<string, { displayName: string; subsystem: string }>();
    (selectedPanel.cells ?? []).forEach((cell) =>
      (cell.subsystems ?? []).forEach((sub) => {
        if (!seen.has(sub.displayName)) seen.set(sub.displayName, sub);
      })
    );
    return Array.from(seen.values());
  }, [selectedPanel]);

  const commitThreshold = useCallback(
    (rowKey: string, levelKey: keyof Thresholds, bound: "min" | "max", value: number | null) => {
      const current =
        filterMode === "face-deck"
          ? allPanels.find((p) => p.panelLabel === rowKey)?.thresholds ?? createEmptyThresholds()
          : subsystemThresholds[rowKey] ?? createEmptyThresholds();

      const newMin = bound === "min" ? value : current[levelKey].min;
      const newMax = bound === "max" ? value : current[levelKey].max;

      const validation = validateThresholdRange(newMin, newMax);
      if (!validation.isValid) {
        showToastMessage("error", validation.error || "Invalid threshold range");
        return;
      }

      const updated: Thresholds = {
        ...current,
        [levelKey]: { ...current[levelKey], [bound]: value },
      };

      if (filterMode === "face-deck") {
        applyPanelThreshold(rowKey, updated);
      } else {
        setSubsystemThreshold(rowKey, updated);
      }
    },
    [filterMode, allPanels, subsystemThresholds, applyPanelThreshold, setSubsystemThreshold]
  );

  const rows: ThresholdRow[] = useMemo(() => {
    if (filterMode === "face-deck") {
      return allPanels.map((panel) => ({
        key: panel.panelLabel,
        label: panel.displayName,
        thresholds: panel.thresholds ?? createEmptyThresholds(),
      }));
    }
    return subsystemsOnSelectedPanel.map((sub) => ({
      key: sub.displayName,
      label: sub.displayName,
      subLabel: sub.subsystem,
      thresholds: subsystemThresholds[sub.displayName] ?? createEmptyThresholds(),
    }));
  }, [filterMode, allPanels, subsystemsOnSelectedPanel, subsystemThresholds]);

  const validateAllThresholdsData = useCallback((): boolean => {
    if (filterMode === "face-deck") {
      for (const panel of allPanels) {
        if (panel.thresholds) {
          const validation = validateAllThresholds(panel.thresholds);
          if (!validation.isValid) {
            showToastMessage("error", `${panel.displayName}: ${validation.error}`);
            return false;
          }
        }
      }
    } else {
      for (const [displayName, thresholds] of Object.entries(subsystemThresholds)) {
        const validation = validateAllThresholds(thresholds);
        if (!validation.isValid) {
          showToastMessage("error", `${displayName}: ${validation.error}`);
          return false;
        }
      }
    }
    return true;
  }, [filterMode, allPanels, subsystemThresholds]);

  const handleConfirmSave = useCallback(() => {
    setIsSaveConfirmationOpen(false);
    if (validateAllThresholdsData() && mission_id && sat_id) {
      originalHandleSave(mission_id, sat_id);
    }
  }, [validateAllThresholdsData, mission_id, sat_id, originalHandleSave]);

  const handleConfirmCancel = useCallback(() => {
    setIsCancelConfirmationOpen(false);
    originalHandleCancel();
  }, [originalHandleCancel]);

  const handleImport = useCallback(async (file: File) => {
    try {
      await originalHandleImport(file);
    } catch {
      showToastMessage("error", "Import failed");
    }
  }, [originalHandleImport]);

  const columnHelper = createColumnHelper<ThresholdRow>();

  const createThresholdAccessor = useCallback((level: typeof THRESHOLD_LEVELS[0], bound: "min" | "max") =>
    columnHelper.accessor((row) => row.thresholds[level.key][bound], {
      id: `${level.key}_${bound}`,
      header: () => (
        <div className="threshold-header threshold-header--sub">
          {bound === "min" ? "Min" : "Max"}
        </div>
      ),
      cell: (info) => (
        <ThresholdCell
          initialValue={info.getValue() as number | null}
          onCommit={(value) => commitThreshold(info.row.original.key, level.key, bound, value)}
          columnId={`${level.key}_${bound}`}
        />
      ),
    })
  , [commitThreshold]);

  const columns = useMemo(() => {
    const labelColumn = columnHelper.display({
      id: "label",
      header: () => (
        <div className="threshold-header threshold-header--bold">
          {filterMode === "face-deck" ? "Face/Deck" : "Subsystem"}
        </div>
      ),
      cell: (info) => (
        <div className="threshold-row-label">
          <span className="threshold-row-label-main">{info.row.original.label}</span>
          {info.row.original.subLabel && <span className="threshold-row-label-sub">{info.row.original.subLabel}</span>}
        </div>
      ),
    });

    const levelGroupColumns = THRESHOLD_LEVELS.map((level) =>
      columnHelper.group({
        id: level.key,
        header: () => (
          <div className="threshold-header threshold-header--group">
            {level.label}
          </div>
        ),
        columns: [createThresholdAccessor(level, "min"), createThresholdAccessor(level, "max")],
      })
    );

    return [labelColumn, ...levelGroupColumns];
  }, [filterMode, createThresholdAccessor]);

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className={`nodeSpecsModal ${!isSatDesignerSidebarOpen ? "left" : "right"} surfaceThresholdsModal`}>
      <ThermalModalHeader
        header={{
          title: "Surface Thresholds",
          isModalMinimized,
          setIsModalMinimized,
          setOpenModal,
          modalItem: "isSurfaceThresholdsModalOpen",
          onExportCsv: handleExportCsv,
          onImport: handleImport,
        }}
      />

      {!isModalMinimized && (
        <>
          <div className="nodeSpecsModal__inputs nodeSpecsModal__inputs--scroll">
            <div className="infocard-units-location-value-div">
              <div className="external-internal-face-thermal-buttons-div">
                <button type="button" onClick={() => setFilterMode("face-deck")} className={filterMode === "face-deck" ? "active" : ""}>
                  Face/Deck Level
                </button>
                <button type="button" onClick={() => setFilterMode("subsystem")} className={filterMode === "subsystem" ? "active" : ""}>
                  Subsystem Level
                </button>
              </div>
            </div>

            {filterMode === "subsystem" && facePanels.length === 0 && deckPanels.length === 0 && (
              <div className="surface-thresholds-body-placeholder">No subsystems placed anywhere on this satellite yet</div>
            )}

            {filterMode === "subsystem" && (facePanels.length > 0 || deckPanels.length > 0) && (
              <div className="infocard-units-location-value-div">
                <div className="external-face-selector-div">
                  {facePanelRows.map((row) => (
                    <div key={row.map((p) => p.panelLabel).join("-")} className="external-face-selector-row">
                      {row.map((panel) => (
                        <PanelSelectorButton
                          panel={panel}
                          isSelected={selectedPanelLabel === panel.panelLabel}
                          isConfigured={isPanelThresholdsConfigured(panel)}
                          onSelect={setSelectedPanelLabel}
                        />
                      ))}
                    </div>
                  ))}
                  {deckPanels.length > 0 && (
                    <div className="external-face-selector-row">
                      {deckPanels.map((panel) => (
                        <PanelSelectorButton
                          panel={panel}
                          isSelected={selectedPanelLabel === panel.panelLabel}
                          isConfigured={isPanelThresholdsConfigured(panel)}
                          onSelect={setSelectedPanelLabel}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="nodeSpecsModal__divider__div">
              <DividerLine />
            </div>

            {!thermalModel && <div className="surface-thresholds-body-placeholder">Loading thermal model…</div>}

            {thermalModel && filterMode === "subsystem" && rows.length === 0 && (
              <div className="surface-thresholds-body-placeholder">No subsystems placed on this face/deck</div>
            )}

            {thermalModel && rows.length > 0 && (
              <table className="surface-thresholds-table">
                <thead>
                  {table.getHeaderGroups().map((headerGroup, groupIndex) => (
                    <tr key={headerGroup.id}>
                      {groupIndex === 0 && <th></th>}
                      {groupIndex === 1 && <th></th>}
                      {headerGroup.headers
                        .filter((h) => !h.isPlaceholder && h.column.id !== "label")
                        .map((headerCell) => (
                          <th key={headerCell.id} colSpan={headerCell.colSpan}>
                            {flexRender(headerCell.column.columnDef.header, headerCell.getContext())}
                          </th>
                        ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="surface-thresholds-td">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <ThermalModalFooter
            onSave={() => setIsSaveConfirmationOpen(true)}
            isLastNode
            onCancel={() => setIsCancelConfirmationOpen(true)}
          />
        </>
      )}

      {createPortal(
        <ConfirmationDialog
          type="update"
          open={isSaveConfirmationOpen}
          title="Save"
          description="Are you sure you want to save this thermal configuration?"
          confirm_button_text="Save"
          onConfirm={handleConfirmSave}
          onCancel={() => setIsSaveConfirmationOpen(false)}
        />,
        document.body
      )}

      {createPortal(
        <ConfirmationDialog
          type="delete"
          open={isCancelConfirmationOpen}
          title="Discard Changes"
          description="Are you sure you want to cancel? Any unsaved changes will be lost."
          confirm_button_text="Discard"
          onConfirm={handleConfirmCancel}
          onCancel={() => setIsCancelConfirmationOpen(false)}
        />,
        document.body
      )}
    </div>
  );
};

export default SurfaceThresholdsModal;
